import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const formatMoney = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('es-MX'); } catch { return iso; }
};

/**
 * Renders a thermal-style 80mm receipt that is hidden on screen and only
 * visible in print mode. Pages embed it once and call window.print() to fire it.
 *
 * Props:
 *   variant: 'sale' | 'repair'
 *   data:    sale or repair object
 *   branchName: string
 *   extra: { paymentLabel } (optional)
 */
export default function ThermalTicket({ variant, data, branchName, extra = {} }) {
  const { getAuthHeaders } = useAuth();
  const [shop, setShop] = useState({});

  useEffect(() => {
    let active = true;
    axios.get(`${API}/api/settings/shop_info`, { headers: getAuthHeaders() })
      .then(res => { if (active) setShop(res.data?.value || {}); })
      .catch(() => { if (active) setShop({}); });
    return () => { active = false; };
  }, [getAuthHeaders]);

  if (!data) return null;

  return (
    <div className="thermal-ticket" data-testid={`thermal-ticket-${variant}`}>
      <Header shop={shop} branchName={branchName} />
      <hr className="tk-divider" />
      {variant === 'sale' ? <SaleBody sale={data} /> : <RepairBody repair={data} />}
      <hr className="tk-divider" />
      <Footer shop={shop} />
    </div>
  );
}

function Header({ shop, branchName }) {
  return (
    <div className="tk-center">
      <div className="tk-lg">{shop.name || 'Fixif'}</div>
      {shop.address && <div className="tk-sm">{shop.address}</div>}
      {shop.phone && <div className="tk-sm">Tel: {shop.phone}</div>}
      {shop.rfc && <div className="tk-sm">RFC: {shop.rfc}</div>}
      {branchName && <div className="tk-sm">Sucursal: {branchName}</div>}
    </div>
  );
}

function Footer({ shop }) {
  return (
    <div className="tk-center tk-sm">
      {shop.footer ? <div>{shop.footer}</div> : <div>Gracias por su preferencia.</div>}
      {shop.website && <div>{shop.website}</div>}
    </div>
  );
}

function SaleBody({ sale }) {
  const items = sale.items || [];
  return (
    <>
      <div className="tk-row tk-bold">
        <span>TICKET</span><span>{sale.ticket || sale.id?.slice(-8)}</span>
      </div>
      <div className="tk-row tk-sm">
        <span>{formatDate(sale.created_at)}</span>
      </div>
      <div className="tk-sm">Cliente: {sale.client_name || 'Venta general'}</div>
      <div className="tk-sm">Atendio: {sale.sold_by || '-'}</div>
      <hr className="tk-divider" />
      <table>
        <tbody>
          {items.map((it, i) => (
            <tr key={i}>
              <td>
                <div>{it.name}</div>
                <div className="tk-sm">{it.quantity} x {formatMoney(it.price)}</div>
              </td>
              <td className="tk-right tk-bold">
                {formatMoney((it.price || 0) * (it.quantity || 1))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <hr className="tk-divider" />
      <div className="tk-row"><span>Subtotal</span><span>{formatMoney(sale.subtotal)}</span></div>
      {(sale.discount_amount || 0) > 0 && (
        <div className="tk-row">
          <span>Descuento {sale.discount_type === 'percentage' ? `(${sale.discount_value}%)` : ''}</span>
          <span>-{formatMoney(sale.discount_amount)}</span>
        </div>
      )}
      <div className="tk-row tk-lg"><span>TOTAL</span><span>{formatMoney(sale.total)}</span></div>
      <div className="tk-row"><span>Pagado ({sale.payment_method})</span><span>{formatMoney(sale.amount_paid)}</span></div>
      {(sale.amount_paid || 0) < (sale.total || 0) && (
        <div className="tk-row tk-bold"><span>Saldo</span><span>{formatMoney((sale.total || 0) - (sale.amount_paid || 0))}</span></div>
      )}
    </>
  );
}

function RepairBody({ repair }) {
  const balance = Math.max(0, (repair.total_cost || 0) - (repair.total_paid || 0));
  return (
    <>
      <div className="tk-center tk-lg">ORDEN DE REPARACION</div>
      <div className="tk-row tk-bold">
        <span>TICKET</span><span>{repair.ticket}</span>
      </div>
      <div className="tk-sm">Recibido: {formatDate(repair.created_at)}</div>
      <hr className="tk-divider" />
      <div className="tk-sm tk-bold">CLIENTE</div>
      <div className="tk-sm">{repair.client_name}</div>
      {repair.client_phone && <div className="tk-sm">Tel: {repair.client_phone}</div>}
      {repair.contact_phone && <div className="tk-sm">Contacto: {repair.contact_phone}</div>}
      <hr className="tk-divider" />
      <div className="tk-sm tk-bold">EQUIPO</div>
      <div className="tk-sm">{repair.device_brand} {repair.device_model}</div>
      <div className="tk-sm tk-bold" style={{ marginTop: 4 }}>FALLA REPORTADA</div>
      <div className="tk-sm">{repair.problem_description}</div>
      {repair.checklist && Object.keys(repair.checklist).length > 0 && (
        <>
          <div className="tk-sm tk-bold" style={{ marginTop: 4 }}>CHECKLIST</div>
          <div className="tk-sm">
            {Object.entries(repair.checklist).map(([k, v]) => (
              <div key={k}>{v ? '[X]' : '[ ]'} {k}</div>
            ))}
          </div>
        </>
      )}
      {repair.wait_time && <div className="tk-sm" style={{ marginTop: 4 }}>Tiempo estimado: {repair.wait_time}</div>}
      {(repair.estimated_cost || 0) > 0 && (
        <div className="tk-sm">Presupuesto: {formatMoney(repair.estimated_cost)}</div>
      )}
      {(repair.items || []).length > 0 && (
        <>
          <hr className="tk-divider" />
          <div className="tk-sm tk-bold">PIEZAS / SERVICIOS</div>
          <table>
            <tbody>
              {repair.items.map((it, i) => (
                <tr key={i}>
                  <td><div className="tk-sm">{it.name}</div><div className="tk-sm">{it.quantity} x {formatMoney(it.price)}</div></td>
                  <td className="tk-right tk-sm tk-bold">{formatMoney(it.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
      <hr className="tk-divider" />
      <div className="tk-row"><span>Total</span><span>{formatMoney(repair.total_cost)}</span></div>
      <div className="tk-row"><span>Pagado</span><span>{formatMoney(repair.total_paid)}</span></div>
      <div className="tk-row tk-bold"><span>Saldo</span><span>{formatMoney(balance)}</span></div>
      {repair.security_code && (
        <div className="tk-code-box">
          <div className="tk-sm">Codigo de seguridad para entrega</div>
          <div className="tk-xl">{repair.security_code}</div>
          <div className="tk-sm">Conserve este ticket</div>
        </div>
      )}
      <hr className="tk-divider" />
      <div className="tk-sm">
        Acepto las condiciones del servicio y autorizo la revision del equipo.
      </div>
      <div style={{ marginTop: 18, borderTop: '1px solid #000', paddingTop: 2 }} className="tk-center tk-sm">
        Firma del cliente
      </div>
    </>
  );
}
